/**
 * MyScriptsPage
 * 
 * Unified page for managing scripts with three tabs:
 * - My Scripts: Performance dashboard with metrics
 * - Editor: Manual script editor (ScriptBuilder)
 * - AI Script Creator: AI script generator (AIScriptBuilder)
 */

import React, { useState, useCallback, useEffect, useRef, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import ScriptsList from '../components/MyScripts/ScriptsList';
import ScriptBuilder from '../components/ScriptBuilder';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import { applyImprovementToGraph } from '../utils/scriptImprovements';
import { saveRevision } from '../utils/templateRevisions';

// Lazy load AIScriptBuilder for code splitting
const AIScriptBuilder = lazyWithRetry(() => import('../components/AIScriptBuilder'), 'AIScriptBuilder');
const ImprovementsSuggester = lazyWithRetry(() => import('../components/AIScriptBuilder/ImprovementsSuggester'), 'ImprovementsSuggester');
const ImpactReport = lazyWithRetry(() => import('../components/AIScriptBuilder/ImpactReport'), 'ImpactReport');

/**
 * MyScriptsPage Component
 */
const MyScriptsPage = ({
  // ScriptBuilder props
  onApplyChanges,
  onUpdateTemplate,
  onDeleteTemplate,
  onSetAsDefault,
  onTemplateSelectionChange,
  availableTemplates,
  currentTemplate,
  defaultTemplate,
  onUnsavedChangesChange,
  onAutoSaveChange,
  onRegisterSave,
  onFlowchartModalChange,
  refreshTemplates,
  // Common props
  onClose,
  templatesLoading,
  db,
  // Analytics for Scripts Performance dashboard
  analytics,
  // Subscription plan for feature gating
  teamSubscriptionPlan
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userRole, userEmail, userTeam } = useAuth();
  const { templates, isLoadingData } = useData();
  
  // State for "Create New Script" dialog
  const [showNewScriptDialog, setShowNewScriptDialog] = useState(false);
  const [newScriptName, setNewScriptName] = useState('');
  const [newScriptNameError, setNewScriptNameError] = useState('');
  const [isCreatingScript, setIsCreatingScript] = useState(false);
  
  // State for AI Improvements view
  const [improveScript, setImproveScript] = useState(null);
  const [improvePerformance, setImprovePerformance] = useState(null);
  const [evaluationCount, setEvaluationCount] = useState(null);
  // lastImprovedAt for the currently selected script (ISO string or null)
  const [improveLastImprovedAt, setImproveLastImprovedAt] = useState(null);
  // 'impact' | 'suggestions' | null (null = show script picker)
  const [improveView, setImproveView] = useState(null);

  // Track whether a pre-change revision has been saved for this improve session
  const revisionSavedForSession = useRef(null);

  // Get initial tab from URL params or default to 'list'
  const initialTab = searchParams.get('tab') || 'list';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Track selected template for editing
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    searchParams.get('template') || currentTemplate
  );
  
  // Sync selectedTemplateId when currentTemplate changes (e.g., after deletion)
  // or when the selected template is no longer available
  useEffect(() => {
    // If selected template no longer exists in available templates, sync with currentTemplate
    if (selectedTemplateId && availableTemplates && !availableTemplates[selectedTemplateId]) {
      console.log('🔄 Selected template no longer available, syncing to:', currentTemplate);
      setSelectedTemplateId(currentTemplate);
    }
    // Also sync if currentTemplate changed and we don't have a valid selection
    else if (!selectedTemplateId && currentTemplate) {
      setSelectedTemplateId(currentTemplate);
    }
  }, [selectedTemplateId, currentTemplate, availableTemplates]);
  
  // Sync activeTab and selectedTemplateId with URL search params
  // (handles navigation from other pages like Marketplace "Open in Script Editor")
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const templateParam = searchParams.get('template');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    if (templateParam && templateParam !== selectedTemplateId) {
      setSelectedTemplateId(templateParam);
      if (onTemplateSelectionChange) {
        onTemplateSelectionChange(templateParam);
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the URL ?template= param in sync whenever the selected template changes
  // while on the edit tab. This ensures refreshing the page always restores the
  // correct template even if the user changed it via the ScriptBuilder dropdown.
  useEffect(() => {
    if (activeTab !== 'edit' || !selectedTemplateId) return;
    const currentUrlTemplate = searchParams.get('template');
    if (currentUrlTemplate !== selectedTemplateId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('template', selectedTemplateId);
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedTemplateId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Track initial node to auto-select in ScriptBuilder (from flowchart "Configure Details")
  const initialNodeId = searchParams.get('node');
  
  // Track if there are unsaved changes in ScriptBuilder
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Local tracking of ScriptBuilder autoSave state and save function
  const [localAutoSave, setLocalAutoSave] = useState(true);
  const localSaveRef = useRef(null);

  const handleAutoSaveChange = useCallback((val) => {
    setLocalAutoSave(val);
    if (onAutoSaveChange) onAutoSaveChange(val);
  }, [onAutoSaveChange]);

  const handleRegisterSave = useCallback((fn) => {
    localSaveRef.current = fn;
    if (onRegisterSave) onRegisterSave(fn);
  }, [onRegisterSave]);

  const isTeamLead = userRole === 'team_lead';
  const isAdmin = userRole === 'admin';
  const canAccessScriptBuilder = isTeamLead || isAdmin;

  const doTabSwitch = useCallback((newTab) => {
    setActiveTab(newTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    if (newTab === 'edit' && selectedTemplateId) {
      newParams.set('template', selectedTemplateId);
    } else {
      newParams.delete('template');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, selectedTemplateId]);

  const handleTabChange = useCallback((newTab) => {
    if (activeTab === 'edit' && hasUnsavedChanges && newTab !== 'edit' && !localAutoSave) {
      toast(
        (t) => (
          <div>
            <p style={{ marginBottom: '12px', fontWeight: 500 }}>You have unsaved changes.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => toast.dismiss(t.id)}
                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={() => { toast.dismiss(t.id); if (refreshTemplates) refreshTemplates(); doTabSwitch(newTab); }}
                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', color: '#DC2626', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  toast.dismiss(t.id);
                  if (localSaveRef.current) {
                    try { await localSaveRef.current(); } catch (e) { console.error('Save before tab switch failed:', e); }
                  }
                  doTabSwitch(newTab);
                }}
                style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Save
              </button>
            </div>
          </div>
        ),
        {
          duration: Infinity,
          position: 'top-center',
          style: {
            background: '#fff', color: '#374151', border: '1px solid #E5E7EB',
            borderRadius: '8px', padding: '20px', fontSize: '14px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            maxWidth: '420px', minWidth: '320px'
          }
        }
      );
      return;
    }
    doTabSwitch(newTab);
  }, [activeTab, hasUnsavedChanges, localAutoSave, doTabSwitch]);
  
  /**
   * Handle editing a script from the list (goes to ScriptBuilder for advanced settings)
   */
  const handleEditScript = useCallback((template) => {
    setSelectedTemplateId(template.id);
    if (onTemplateSelectionChange) {
      onTemplateSelectionChange(template.id);
    }
    setActiveTab('edit');
    
    // Update URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'edit');
    newParams.set('template', template.id);
    setSearchParams(newParams, { replace: true });
  }, [onTemplateSelectionChange, searchParams, setSearchParams]);
  
  /**
   * Handle editing a script in flowchart (visual editor - PRIMARY action)
   */
  const handleEditFlowchart = useCallback((script) => {
    // Navigate directly to the flowchart editor
    navigate(`/flowchart-editor/${script.id}`);
  }, [navigate]);
  
  /**
   * Handle creating a new script (manual) - show name dialog first
   */
  const handleCreateNew = useCallback(() => {
    // Generate a default name
    const timestamp = new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
    setNewScriptName(`My Script - ${timestamp}`);
    setNewScriptNameError('');
    setShowNewScriptDialog(true);
  }, []);

  /**
   * Actually create the blank script after name is confirmed
   */
  const handleConfirmCreateNew = useCallback(async () => {
    if (!newScriptName.trim()) {
      setNewScriptNameError('Please enter a name for your script');
      return;
    }

    // Check for duplicate names
    const existingNames = Object.values(availableTemplates || {})
      .map(t => t.name?.toLowerCase());
    if (existingNames.includes(newScriptName.toLowerCase())) {
      setNewScriptNameError('A script with this name already exists');
      return;
    }

    setIsCreatingScript(true);
    try {
      // Create a minimal blank template
      const blankGraph = {
        nodes: {
          opening: {
            id: 'opening',
            script: 'Hi, this is [Your Name]. How can I help you today?',
            coaching: 'Start with a friendly greeting.',
            responses: {
              interested: { 
                nextNode: '__end_script__',
                data: { interested: true },
                label: 'Interested'
              },
              not_interested: { 
                nextNode: '__end_script__',
                data: { interested: false },
                label: 'Not Interested'
              }
            },
            step: 'opening',
            isOpeningNode: true
          }
        },
        ui: {
          positions: {
            opening: { x: 100, y: 100 }
          }
        }
      };

      const blankSteps = [
        { id: 'opening', name: 'Opening', description: 'Initial greeting' },
        { id: 'closing', name: 'Closing', description: 'End of call' }
      ];

      const templateData = {
        graph: blankGraph,
        steps: blankSteps,
        prospectForm: { fields: [] }
      };

      // Create the new template
      await onApplyChanges(templateData, newScriptName.trim());
      
      // Close dialog
      setShowNewScriptDialog(false);
      setNewScriptName('');
      
      // Refresh templates to get the new one
      if (refreshTemplates) {
        await refreshTemplates();
      }

      // Navigate to edit tab - the new template should auto-select
      setActiveTab('edit');
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'edit');
      setSearchParams(newParams, { replace: true });

    } catch (error) {
      console.error('Error creating blank script:', error);
      setNewScriptNameError('Failed to create script. Please try again.');
    } finally {
      setIsCreatingScript(false);
    }
  }, [newScriptName, availableTemplates, onApplyChanges, refreshTemplates, searchParams, setSearchParams]);
  
  /**
   * Handle creating with AI
   */
  const handleCreateWithAI = useCallback(() => {
    setActiveTab('ai');
    
    // Update URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'ai');
    newParams.delete('template');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);
  
  /**
   * Handle navigating to marketplace
   */
  const handleBrowseMarketplace = useCallback(() => {
    navigate('/marketplace');
  }, [navigate]);


  /**
   * Handle "Improve" action from ScriptsList
   */
  const handleImproveScript = useCallback((script) => {
    const lastImprovedAt = script.template?.lastImprovedAt || null;
    setImproveScript(script);
    setImproveLastImprovedAt(lastImprovedAt);
    setImprovePerformance({
      calls: script.calls || 0,
      leads: script.leads || 0,
      rate: script.rate || 0,
      dropoffs: script.dropoffs || 0,
      topResetPoints: script.topResetPoints || [],
    });
    // If the script has been improved before, show the Impact Report first.
    // Otherwise go straight to the suggestions view.
    setImproveView(lastImprovedAt ? 'impact' : 'suggestions');
    // Load scoped evaluation count (only evals since last improvement)
    if (analytics) {
      setEvaluationCount(null); // show loading state while fetching
      analytics.getEvaluationCountSince(script.id, lastImprovedAt).then((count) => {
        setEvaluationCount(count);
      }).catch(() => setEvaluationCount(0));
    }
    setActiveTab('improve');
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'improve');
    newParams.set('template', script.id);
    setSearchParams(newParams, { replace: true });
  }, [analytics, searchParams, setSearchParams]);

  /**
   * From the ImpactReport, user wants to run AI Improve again.
   */
  const handleImproveAgain = useCallback(() => {
    setImproveView('suggestions');
  }, []);

  /**
   * From the ImprovementsSuggester, back button.
   * If there's an impact view (script was previously improved), go back to it.
   * Otherwise go back to the script picker.
   */
  const handleSuggestionsBack = useCallback(() => {
    if (improveLastImprovedAt) {
      setImproveView('impact');
    } else {
      setImproveScript(null);
      setImprovePerformance(null);
      setImproveLastImprovedAt(null);
      setEvaluationCount(null);
      setImproveView(null);
      revisionSavedForSession.current = null;
    }
  }, [improveLastImprovedAt]);

  /**
   * Apply a single AI improvement suggestion to the template graph
   */
  const handleApplyImprovement = useCallback(async (suggestion) => {
    if (!improveScript?.id || !availableTemplates) return;
    const template = availableTemplates[improveScript.id];
    if (!template) return;

    const graph = template.graph || template.data?.scriptGraph || {};

    // Save a revision snapshot before the first change in this session
    if (revisionSavedForSession.current !== improveScript.id && userTeam) {
      revisionSavedForSession.current = improveScript.id;
      saveRevision(userTeam, improveScript.id, template, {
        reason: 'Before AI improvements',
        triggeredBy: 'ai_improve',
        userEmail,
      });
    }

    const { graph: updatedGraph, applied, error } = applyImprovementToGraph(graph, suggestion);

    if (!applied) {
      console.warn('Could not apply suggestion:', error);
      toast.error(error || 'Could not apply this suggestion');
      return;
    }

    const newLastImprovedAt = new Date().toISOString();
    const updatedTemplateData = {
      graph: updatedGraph,
      steps: template.steps,
      prospectForm: template.prospectForm,
      lastImprovedAt: newLastImprovedAt,
    };

    // Keep local state in sync so the UI label reflects the improvement immediately
    setImproveLastImprovedAt(newLastImprovedAt);

    if (onUpdateTemplate) {
      onUpdateTemplate(improveScript.id, updatedTemplateData, template.name, false, {
        reason: 'AI Improved',
        triggeredBy: 'ai_improve',
      });
    }
  }, [improveScript, availableTemplates, onUpdateTemplate, userTeam, userEmail]);

  /**
   * Apply multiple AI improvement suggestions in a single save (one revision)
   */
  const handleApplyImprovementBulk = useCallback(async (suggestions) => {
    if (!improveScript?.id || !availableTemplates || !suggestions?.length) return;
    const template = availableTemplates[improveScript.id];
    if (!template) return;

    // Apply all suggestions to the graph sequentially
    let currentGraph = template.graph || template.data?.scriptGraph || {};
    let appliedCount = 0;
    for (const suggestion of suggestions) {
      const { graph: updatedGraph, applied } = applyImprovementToGraph(currentGraph, suggestion);
      if (applied) {
        currentGraph = updatedGraph;
        appliedCount++;
      }
    }

    if (appliedCount === 0) return;

    const newLastImprovedAt = new Date().toISOString();
    const updatedTemplateData = {
      graph: currentGraph,
      steps: template.steps,
      prospectForm: template.prospectForm,
      lastImprovedAt: newLastImprovedAt,
    };

    // Keep local state in sync so the UI label reflects the improvement immediately
    setImproveLastImprovedAt(newLastImprovedAt);

    if (onUpdateTemplate) {
      onUpdateTemplate(improveScript.id, updatedTemplateData, template.name, false, {
        reason: `AI Improved (${appliedCount} suggestion${appliedCount !== 1 ? 's' : ''})`,
        triggeredBy: 'ai_improve',
      });
    }
  }, [improveScript, availableTemplates, onUpdateTemplate]);

  /**
   * Close the improve view — go back to the script picker
   */
  const handleCloseImprove = useCallback(() => {
    setImproveScript(null);
    setImprovePerformance(null);
    setImproveLastImprovedAt(null);
    setEvaluationCount(null);
    setImproveView(null);
    revisionSavedForSession.current = null;
  }, []);

  /**
   * Restore a template revision — called from RevisionHistory inside ScriptDetailPanel
   * The revision object contains the templateId it belongs to.
   */
  const handleRestoreRevision = useCallback(async (revision, templateId) => {
    if (!templateId || !availableTemplates?.[templateId] || !userTeam) return;
    const cur = availableTemplates[templateId];

    await saveRevision(userTeam, templateId, cur, {
      reason: 'Before restore',
      triggeredBy: 'restore',
      userEmail,
    });

    const restoredData = {
      graph: revision.graph || {},
      steps: revision.steps || cur.steps,
      prospectForm: revision.prospectForm || cur.prospectForm,
    };

    if (onUpdateTemplate) {
      onUpdateTemplate(templateId, restoredData, cur.name, false, { reason: 'After restore', triggeredBy: 'restore' });
      toast.success('Restored to previous version');
    }
  }, [availableTemplates, userTeam, userEmail, onUpdateTemplate]);

  /**
   * Handle unsaved changes state
   */
  const handleUnsavedChangesChange = useCallback((hasChanges) => {
    setHasUnsavedChanges(hasChanges);
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(hasChanges);
    }
  }, [onUnsavedChangesChange]);
  
  // Access check
  if (!canAccessScriptBuilder) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">
            Only Team Leads and Admins can manage scripts.
          </p>
        </div>
      </div>
    );
  }
  
  // Get script count for tab label
  const scriptCount = Object.keys(templates || {}).length;
  
  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Create New Script Dialog */}
      {showNewScriptDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => !isCreatingScript && setShowNewScriptDialog(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Create New Script
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Enter a name for your new script. You can change it later.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Script Name
              </label>
              <input
                type="text"
                value={newScriptName}
                onChange={(e) => {
                  setNewScriptName(e.target.value);
                  setNewScriptNameError('');
                }}
                placeholder="Enter script name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isCreatingScript) {
                    handleConfirmCreateNew();
                  }
                }}
              />
              {newScriptNameError && (
                <p className="text-red-500 text-xs mt-1">{newScriptNameError}</p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleConfirmCreateNew}
                disabled={isCreatingScript || !newScriptName.trim()}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {isCreatingScript ? 'Creating...' : 'Create Script'}
              </button>
              <button
                onClick={() => setShowNewScriptDialog(false)}
                disabled={isCreatingScript}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Title - matching sidebar header height */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 h-14 bg-white">
        <h2 className="text-xl font-semibold text-blue-600 border-b-2 border-blue-600 pb-1">Script Builder</h2>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white px-4 pt-1.5">
        <Breadcrumbs items={[
          { label: 'Home', href: '/call-script' },
          { label: 'Script Builder', href: '/my-scripts' },
          ...(activeTab !== 'list' ? [{
            label: activeTab === 'edit' ? 'Edit Script' : activeTab === 'improve' ? 'AI Improvements' : 'AI Script Creator',
            current: true
          }] : [])
        ]} />
      </div>
      
      {/* Tab Navigation */}
      <div className="flex space-x-1 border-b border-gray-200 bg-white px-4">
        <button
          onClick={() => handleTabChange('list')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'list'
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          My Scripts ({scriptCount})
        </button>
        <button
          onClick={() => handleTabChange('edit')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'edit'
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          Edit Script
          {hasUnsavedChanges && activeTab === 'edit' && (
            <span className="ml-2 w-2 h-2 bg-orange-500 rounded-full inline-block" title="Unsaved changes" />
          )}
        </button>
        <button
          onClick={() => handleTabChange('ai')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5 ${
            activeTab === 'ai'
              ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          AI Script Creator
          {teamSubscriptionPlan === 'starter' && (
            <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-semibold">Pro</span>
          )}
        </button>
        <button
          onClick={() => handleTabChange('improve')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5 ${
            activeTab === 'improve'
              ? 'bg-green-100 text-green-700 border-b-2 border-green-600'
              : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
          }`}
        >
          AI Improve
          {teamSubscriptionPlan === 'starter' && (
            <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-semibold">Pro</span>
          )}
        </button>
      </div>
      
      {/* Tab content */}
      <div className="flex-1 overflow-auto p-4 bg-gray-50">
        {/* My Scripts Tab */}
        {activeTab === 'list' && (
          <ScriptsList
            onEditScript={handleEditScript}
            onEditFlowchart={handleEditFlowchart}
            onCreateNew={handleCreateNew}
            onCreateWithAI={handleCreateWithAI}
            onBrowseMarketplace={handleBrowseMarketplace}
            onImproveScript={handleImproveScript}
            onRestoreRevision={handleRestoreRevision}
            defaultTemplateId={defaultTemplate}
            analytics={analytics}
            evaluationCount={evaluationCount}
          />
        )}
        
        {/* Editor Tab */}
        {activeTab === 'edit' && (
          <div className="bg-white rounded-lg border border-gray-200">
            {templatesLoading ? (
              <div className="flex justify-center p-12">
                <LoadingSpinner message="Loading templates..." />
              </div>
            ) : (
              <ScriptBuilder
                key={`${userTeam}-${initialNodeId || ''}`}
                onClose={() => handleTabChange('list')}
                onApplyChanges={onApplyChanges}
                onUpdateTemplate={onUpdateTemplate}
                onDeleteTemplate={onDeleteTemplate}
                onSetAsDefault={onSetAsDefault}
                onTemplateSelectionChange={(templateId) => {
                  setSelectedTemplateId(templateId);
                  if (onTemplateSelectionChange) {
                    onTemplateSelectionChange(templateId);
                  }
                }}
                availableTemplates={availableTemplates}
                currentTemplate={selectedTemplateId || currentTemplate}
                defaultTemplate={defaultTemplate}
                onUnsavedChangesChange={handleUnsavedChangesChange}
                onAutoSaveChange={handleAutoSaveChange}
                onRegisterSave={handleRegisterSave}
                onFlowchartModalChange={onFlowchartModalChange}
                refreshTemplates={refreshTemplates}
                initialNodeId={initialNodeId}
                teamSubscriptionPlan={teamSubscriptionPlan}
              />
            )}
          </div>
        )}
        
        {/* Create with AI Tab */}
        {activeTab === 'ai' && (
          teamSubscriptionPlan === 'starter' ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-8">
              <div className="text-4xl mb-3">🤖</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">AI Script Builder is a Pro feature</h3>
              <p className="text-sm text-gray-500 mb-4 max-w-sm">Generate complete call scripts from scratch using AI. Upgrade to Pro to unlock this and AI Improve.</p>
              <a href="/settings?tab=subscription" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                Upgrade to Pro →
              </a>
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="flex justify-center items-center h-64">
                  <div className="text-center">
                    <div className="w-12 h-12 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="mt-4 text-gray-600">Loading AI Script Builder...</p>
                  </div>
                </div>
              }
            >
              <AIScriptBuilder key={userTeam} />
            </Suspense>
          )
        )}

        {/* AI Improve Tab */}
        {activeTab === 'improve' && (
          teamSubscriptionPlan === 'starter' ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-8">
              <div className="text-4xl mb-3">✨</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">AI Script Improvement is a Pro feature</h3>
              <p className="text-sm text-gray-500 mb-4 max-w-sm">Let AI analyze your call evaluations and suggest specific improvements to your script every week. Upgrade to Pro to unlock.</p>
              <a href="/settings?tab=subscription" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                Upgrade to Pro →
              </a>
            </div>
          ) : (
          <>
            {/* ── Impact Report (previously-improved script selected) ── */}
            {improveScript && improveView === 'impact' && (
              <Suspense
                fallback={
                  <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                      <div className="w-12 h-12 border-3 border-green-600 border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="mt-4 text-gray-600">Loading impact report...</p>
                    </div>
                  </div>
                }
              >
                <ImpactReport
                  key={improveScript.id}
                  templateId={improveScript.id}
                  templateName={improveScript.name}
                  lastImprovedAt={improveLastImprovedAt}
                  analytics={analytics}
                  onImproveAgain={handleImproveAgain}
                  onViewChanges={() => handleTabChange('list')}
                  onBack={handleCloseImprove}
                />
              </Suspense>
            )}

            {/* ── Suggestions view ── */}
            {improveScript && improveView === 'suggestions' && (
              <Suspense
                fallback={
                  <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                      <div className="w-12 h-12 border-3 border-green-600 border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="mt-4 text-gray-600">Loading AI Improvements...</p>
                    </div>
                  </div>
                }
              >
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <ImprovementsSuggester
                    key={improveScript.id}
                    templateId={improveScript.id}
                    templateName={improveScript.name}
                    performance={improvePerformance}
                    evaluationCount={evaluationCount}
                    lastImprovedAt={improveLastImprovedAt}
                    onApplyChanges={handleApplyImprovement}
                    onApplyBulk={handleApplyImprovementBulk}
                    onClose={handleSuggestionsBack}
                    analytics={analytics}
                  />
                </div>
              </Suspense>
            )}

            {/* ── Script picker (no script selected yet) ── */}
            {!improveScript && (
              <ImproveScriptPicker
                templates={availableTemplates || templates}
                isLoading={isLoadingData}
                onSelect={handleImproveScript}
                scriptMetrics={null}
                analytics={analytics}
              />
            )}
          </>
          )
        )}
      </div>
    </div>
  );
};

/**
 * ImproveScriptPicker - shown on the Improve tab when no script is selected yet
 */
const ImproveScriptPicker = ({ templates, isLoading, onSelect, analytics }) => {
  const templateList = Object.values(templates || {});
  const [metrics, setMetrics] = React.useState({});
  const [loadingMetrics, setLoadingMetrics] = React.useState(false);

  React.useEffect(() => {
    if (!analytics) return;
    let cancelled = false;
    setLoadingMetrics(true);
    // Build per-template cutoff dates so drop-off counts only reflect data since last improvement
    const perTemplateSince = {};
    templateList.forEach(t => {
      if (t.lastImprovedAt) perTemplateSince[t.id] = new Date(t.lastImprovedAt);
    });
    analytics.getScriptPerformance(null, null, perTemplateSince).then((m) => {
      if (!cancelled) { setMetrics(m); setLoadingMetrics(false); }
    }).catch(() => { if (!cancelled) setLoadingMetrics(false); });
    return () => { cancelled = true; };
  }, [analytics]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <div className="flex items-center justify-center gap-2 text-gray-400">
          <svg className="animate-spin" width="16" height="16" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-sm">Loading scripts...</span>
        </div>
      </div>
    );
  }

  if (templateList.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <p className="text-gray-500">No scripts available. Create a script first.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-gray-800">AI Script Improvements</h3>
        <p className="text-sm text-gray-500 mt-1">
          Select a script to analyze. AI will review call evaluations and suggest specific improvements.
        </p>
      </div>
      <div className="space-y-2">
        {templateList.map((template) => {
          const m = metrics[template.id] || {};
          const hasImpactReport = !!template.lastImprovedAt;
          const lastImprovedDate = hasImpactReport
            ? new Date(template.lastImprovedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : null;
          return (
            <button
              key={template.id}
              onClick={() => {
                const totalDropoffs = (m.topResetPoints || []).reduce((s, p) => s + p.count, 0);
                onSelect({
                  id: template.id,
                  name: template.name || template.id,
                  calls: m.calls || 0,
                  leads: m.leads || 0,
                  rate: m.rate || 0,
                  dropoffs: totalDropoffs,
                  topResetPoints: m.topResetPoints || [],
                  template,
                });
              }}
              className={`w-full flex items-center justify-between p-3 rounded-md border transition-colors text-left group ${
                hasImpactReport
                  ? 'border-green-200 bg-green-50/40 hover:border-green-400 hover:bg-green-50'
                  : 'border-gray-200 hover:border-green-400 hover:bg-green-50'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-800 truncate">{template.name || template.id}</span>
                  {hasImpactReport && (
                    <span className="flex-shrink-0 flex items-center gap-1 text-xs text-green-700 bg-green-100 border border-green-200 px-1.5 py-0.5 rounded-full">
                      <span>📈</span>
                      Impact Report
                    </span>
                  )}
                </div>
                {!loadingMetrics && (
                  <div className="text-xs text-gray-400 mt-0.5 flex gap-3">
                    <span>{m.calls || 0} calls</span>
                    <span>{m.leads || 0} leads</span>
                    {m.rate > 0 && <span>{m.rate}% conv.</span>}
                    {hasImpactReport && (
                      <span className="text-green-600">Improved {lastImprovedDate}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                {hasImpactReport && (
                  <span className="text-xs text-green-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    View report
                  </span>
                )}
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  className="text-gray-300 group-hover:text-green-500 transition-colors">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MyScriptsPage;
